import cn from 'classnames'
import { CheckCircle02Icon } from '@forwardflow/icons'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { useSetupSummary } from './useSetupSummary'

/**
 * The left-hand column: every step of the setup as a stacked box. Done
 * steps show what was decided and can be reopened; the current one is
 * lit; the ones ahead say what they will ask. The right-hand column only
 * ever shows the step being configured.
 */
export const SetupRail = () => {
   const { goToStep, onboardingSaveBlocked } = useOnboardingStoreV2()
   const { rail, stepNumber, total } = useSetupSummary()

   return (
      <nav
         aria-label="Setup progress"
         className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 md:p-4"
         data-testid="setup-rail"
      >
         <div className="flex items-baseline justify-between px-1 pb-3">
            <p className="text-white font-semibold text-sm">Your setup</p>
            <p className="text-white/45 text-xs" data-testid="rail-count">
               {Math.min(stepNumber - 1, total)} of {total} done
            </p>
         </div>
         <ol className="space-y-1.5">
            {rail.map((item, i) => {
               const isCurrent = item.state === 'current'
               const isDone = item.state === 'done'
               const content = (
                  <>
                     <span
                        className={cn(
                           'flex shrink-0 items-center justify-center w-6 h-6 rounded-full text-[11px] font-semibold',
                           isDone && 'text-[#00D492]',
                           isCurrent && 'bg-[#9F7BFF] text-white',
                           item.state === 'upcoming' && 'border border-white/15 text-white/40'
                        )}
                     >
                        {isDone ? <CheckCircle02Icon width={22} height={22} /> : i + 1}
                     </span>
                     <span className="flex-1 min-w-0">
                        <span className="flex items-center justify-between gap-2">
                           <span
                              className={cn(
                                 'text-[13px] font-medium truncate',
                                 isCurrent ? 'text-white' : isDone ? 'text-white/90' : 'text-white/45'
                              )}
                           >
                              {item.title}
                           </span>
                           {isCurrent && (
                              <span className="text-[10px] uppercase tracking-wide text-[#C9B8FF] font-semibold">
                                 Now
                              </span>
                           )}
                           {isDone && item.clickable && (
                              <span className="text-[11px] text-white/40 group-hover:text-[#A684FF]">
                                 Edit
                              </span>
                           )}
                        </span>
                        {item.lines.length ? (
                           <span className="block mt-0.5 space-y-0.5">
                              {item.lines.map((line, j) => (
                                 <span
                                    key={j}
                                    className={cn(
                                       'block text-xs truncate',
                                       j === 0 ? 'text-white/70' : 'text-white/45'
                                    )}
                                 >
                                    {line}
                                 </span>
                              ))}
                           </span>
                        ) : (
                           <span
                              className={cn(
                                 'block text-xs mt-0.5 truncate',
                                 isCurrent ? 'text-white/60' : 'text-white/35'
                              )}
                           >
                              {item.ask}
                           </span>
                        )}
                     </span>
                  </>
               )
               const cls = cn(
                  'group w-full flex items-start gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
                  isCurrent && 'bg-[#9F7BFF]/10 border border-[#9F7BFF]/40',
                  !isCurrent && 'border border-transparent',
                  isDone && item.clickable && 'hover:bg-white/[0.05] cursor-pointer'
               )
               return (
                  <li key={item.id} data-testid={`rail-${item.id}`} data-state={item.state}>
                     {item.clickable ? (
                        <button
                           type="button"
                           className={cls}
                           disabled={Boolean(onboardingSaveBlocked)}
                           onClick={() => goToStep(item.steps[0])}
                           aria-label={`Edit ${item.title}`}
                        >
                           {content}
                        </button>
                     ) : (
                        <div className={cls} aria-current={isCurrent ? 'step' : undefined}>
                           {content}
                        </div>
                     )}
                  </li>
               )
            })}
         </ol>
      </nav>
   )
}
